import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { User } from "oidc-client-ts";
import { userManager, signIn, signOut, currentUser } from "./oidc";

export type Session = {
  user: User | null;
  loading: boolean;
  /** The realm roles on the access token. */
  roles: string[];
  isAdmin: boolean;
  username: string;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
};

const Ctx = createContext<Session | null>(null);

/** Realm roles, read off the access token so the nav knows what to show. */
function rolesOf(user: User | null): string[] {
  if (!user?.access_token) return [];
  try {
    const part = user.access_token.split(".")[1];
    if (!part) return [];
    const json = atob(part.replace(/-/g, "+").replace(/_/g, "/"));
    const claims = JSON.parse(json) as { realm_access?: { roles?: string[] } };
    return claims.realm_access?.roles ?? [];
  } catch {
    // Reading the token here is a convenience for the UI only -- the API
    // verifies it properly, and a token this cannot parse is one the API will
    // refuse anyway.
    return [];
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    currentUser().then((u) => {
      if (alive) {
        setUser(u);
        setLoading(false);
      }
    });

    const loaded = (u: User) => setUser(u);
    const unloaded = () => setUser(null);
    userManager.events.addUserLoaded(loaded);
    userManager.events.addUserUnloaded(unloaded);
    // If the silent renew fails there is no valid token left, and pretending
    // otherwise just produces 401s with no explanation.
    userManager.events.addSilentRenewError(unloaded);
    userManager.events.addAccessTokenExpired(unloaded);

    return () => {
      alive = false;
      userManager.events.removeUserLoaded(loaded);
      userManager.events.removeUserUnloaded(unloaded);
      userManager.events.removeSilentRenewError(unloaded);
      userManager.events.removeAccessTokenExpired(unloaded);
    };
  }, []);

  const value = useMemo<Session>(() => {
    const roles = rolesOf(user);
    return {
      user,
      loading,
      roles,
      isAdmin: roles.includes("admin"),
      username: (user?.profile?.preferred_username as string) ?? "",
      signIn,
      signOut,
    };
  }, [user, loading]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): Session {
  const s = useContext(Ctx);
  if (!s) throw new Error("useAuth used outside AuthProvider");
  return s;
}
